'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { FileSearchIcon, HelpCircleIcon } from '@terra/ui-icons';
import { isValidDomainHost } from '@terra/shared-contracts-applications';
import { DomainSourceSchema } from '@terra/shared-schemas-orchestrator';
import { ApplicationAutomationIds, automationId } from '@terra/shared-utils';
import { BodyStrong, MetaDefault } from '../../components/typography';
import { Icon } from '../../components/icon';
import { Tooltip } from '../../components/tooltip';
import { ConfirmationDialog } from '../confirmation-dialog';
import { SettingsEmptyState } from '../settings/SettingsEmptyState';
import { TableGrid } from '../table-grid';
import { AddDomainDialog } from './AddDomainDialog';
import { AddPathsDialog } from './AddPathsDialog';
import { buildDomainScopeTableRows } from './domain-scope-table-rows';
import {
  addPathsToDomain,
  applyFunctionalityToggle,
  applyPrimaryHostAttackToggle,
  applyTestingToggle,
  applyThirdPartyToggle,
  buildAddedDomainEntry,
  getApplicationUrlHostname,
  getDomainConfigHostname,
  getPathEntries,
  getPrimaryHostDomainEntry,
  getPrimaryHostRedirectTargetDomain,
  getScopeFlag,
  getSecondaryDomainsForPrimaryHost,
  isDomainLinkedToPrimaryHost,
  removePathFromDomain,
  resolvePrimaryHostAttackEnabled,
  updatePathInDomain,
  updatePrimaryHostDomainEntry,
  type DomainConfig,
  type PathScopeDraft,
  type PathScopeEntry,
  type PathScopeKind,
} from './domain-scope.utils';

const buildScopedColumnTitle = ({
  label,
  tooltip,
  dataTestid,
}: {
  label: string;
  tooltip: string;
  dataTestid: string;
}) => (
  <HStack gap={1} alignItems="center" {...automationId(dataTestid)}>
    <MetaDefault color="fg.neutral.subtle">{label}</MetaDefault>
    <Tooltip title={tooltip}>
      <Icon asChild color="fg.muted" width={3.5} height={3.5}>
        <HelpCircleIcon />
      </Icon>
    </Tooltip>
  </HStack>
);

export const useDomainScopeTableColumns = () => {
  const t = useTranslations('designSystem.domainScope');

  return [
    {
      id: 'expand',
      title: '',
      width: '16px',
      truncate: false,
      align: 'center' as const,
      cellPx: '2',
      hideHeaderDividerAfter: true,
    },
    {
      id: 'domainPath',
      width: 'minmax(200px, 1fr)',
      title: t('columns.domainPath'),
      truncate: false,
    },
    {
      id: 'source',
      title: t('columns.source'),
      width: 'minmax(80px, max-content)',
    },
    {
      id: 'thirdParty',
      width: 'minmax(110px, max-content)',
      truncate: false,
      title: buildScopedColumnTitle({
        label: t('columns.thirdParty'),
        tooltip: t('tooltips.thirdParty'),
        dataTestid: ApplicationAutomationIds.domainScopeColumnThirdParty,
      }),
    },
    {
      id: 'functionality',
      width: 'minmax(88px, max-content)',
      truncate: false,
      title: buildScopedColumnTitle({
        label: t('columns.functionality'),
        tooltip: t('tooltips.functionality'),
        dataTestid: ApplicationAutomationIds.domainScopeColumnInteraction,
      }),
    },
    {
      id: 'testing',
      width: 'minmax(72px, max-content)',
      truncate: false,
      title: buildScopedColumnTitle({
        label: t('columns.testing'),
        tooltip: t('tooltips.testing'),
        dataTestid: ApplicationAutomationIds.domainScopeColumnAttack,
      }),
    },
    {
      id: 'pathScope',
      title: t('columns.pathScope'),
      width: 'minmax(100px, max-content)',
      truncate: false,
    },
    {
      id: 'actions',
      title: t('columns.actions'),
      width: '72px',
      truncate: false,
      align: 'end' as const,
      cellPx: '2',
    },
  ];
};

export const useDomainScopeTableDerived = ({
  domains,
  appUrlHostname,
  primaryApplicationUrl,
  redirectToUrl,
}: {
  domains: DomainConfig[];
  appUrlHostname?: string | null;
  primaryApplicationUrl?: string | null;
  redirectToUrl?: string | null;
}) => {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set(),
  );
  const [isPrimaryExpanded, setIsPrimaryExpanded] = useState(false);
  const trimmedPrimaryApplicationUrl = primaryApplicationUrl?.trim() ?? '';
  const primaryHostname =
    getApplicationUrlHostname(trimmedPrimaryApplicationUrl) ??
    appUrlHostname ??
    null;
  const primaryHostDomainEntry = primaryHostname
    ? getPrimaryHostDomainEntry({ domains, hostname: primaryHostname })
    : undefined;
  const primaryPathEntries = useMemo(
    () =>
      primaryHostDomainEntry ? getPathEntries(primaryHostDomainEntry) : [],
    [primaryHostDomainEntry],
  );
  const primaryAttackEnabled = primaryHostname
    ? resolvePrimaryHostAttackEnabled({
        applicationUrl: trimmedPrimaryApplicationUrl,
        domains,
      })
    : true;
  const primaryRedirectTargetDomain = useMemo(
    () =>
      getPrimaryHostRedirectTargetDomain({
        domains,
        hostname: primaryHostname,
        redirectToUrl,
      }),
    [domains, primaryHostname, redirectToUrl],
  );
  const redirectTargetHostname = primaryRedirectTargetDomain
    ? getDomainConfigHostname(primaryRedirectTargetDomain.domain)
    : null;
  const secondaryDomains = useMemo(
    () =>
      getSecondaryDomainsForPrimaryHost({
        domains,
        hostname: primaryHostname,
        redirectToUrl,
      }),
    [domains, primaryHostname, redirectToUrl],
  );
  const previousPrimaryPathCountRef = useRef<number | null>(null);
  const previousSecondaryPathCountsRef = useRef<Record<string, number> | null>(
    null,
  );

  useEffect(() => {
    const pathCount = primaryPathEntries.length;
    const previousCount = previousPrimaryPathCountRef.current;

    previousPrimaryPathCountRef.current = pathCount;
    if (pathCount === 0) {
      return;
    }

    if (previousCount === null || previousCount === 0) {
      setIsPrimaryExpanded(true);
    }
  }, [primaryPathEntries.length]);
  useEffect(() => {
    const currentCounts: Record<string, number> = {};

    for (const entry of secondaryDomains) {
      currentCounts[entry.domain] = getPathEntries(entry).length;
    }

    const previousCounts = previousSecondaryPathCountsRef.current;

    previousSecondaryPathCountsRef.current = currentCounts;
    const domainsToExpand =
      previousCounts === null
        ? Object.entries(currentCounts)
            .filter(([, count]) => count > 0)
            .map(([domainName]) => domainName)
        : Object.entries(currentCounts)
            .filter(
              ([domainName, count]) =>
                count > 0 && (previousCounts[domainName] ?? 0) === 0,
            )
            .map(([domainName]) => domainName);

    if (domainsToExpand.length === 0) {
      return;
    }

    setExpandedDomains((previous) => {
      const next = new Set(previous);
      let changed = false;

      for (const domainName of domainsToExpand) {
        if (!next.has(domainName)) {
          next.add(domainName);
          changed = true;
        }
      }

      return changed ? next : previous;
    });
  }, [secondaryDomains]);

  return {
    trimmedPrimaryApplicationUrl,
    primaryHostname,
    primaryHostDomainEntry,
    primaryPathEntries,
    primaryAttackEnabled,
    redirectTargetHostname,
    secondaryDomains,
    isPrimaryExpanded,
    setIsPrimaryExpanded,
    expandedDomains,
    setExpandedDomains,
  };
};

type PathsDialogState = {
  domain: string;
  editPath?: string;
  editKind?: PathScopeKind;
  editIsInFunctionalityScope?: boolean;
  editIsInTestingScope?: boolean;
  editIsThirdParty?: boolean;
} | null;

type PathToRemove = {
  domain: string;
  path: string;
};

type UseDomainScopeTableViewParams = {
  domains: DomainConfig[];
  discoveredSubdomains: string[];
  trimmedPrimaryApplicationUrl: string;
  primaryHostname: string | null;
  primaryHostDomainEntry?: DomainConfig;
  redirectTargetHostname: string | null;
  primaryPathEntries: PathScopeEntry[];
  primaryAttackEnabled: boolean;
  secondaryDomains: DomainConfig[];
  isPrimaryExpanded: boolean;
  setIsPrimaryExpanded: (
    value: boolean | ((current: boolean) => boolean),
  ) => void;
  expandedDomains: Set<string>;
  isReadOnly?: boolean;
  renderDomainSuffix?: (domain: string) => ReactNode;
  pathsDialogState: PathsDialogState;
  domainToRemove: string | null;
  pathToRemove: PathToRemove | null;
  pendingTestingEnableDomain: string | null;
  isDisablePrimaryAttackDialogOpen: boolean;
  setIsDisablePrimaryAttackDialogOpen: (open: boolean) => void;
  toggleExpanded: (domain: string) => void;
  handlePrimaryAttackToggle: () => void;
  handleFunctionalityToggle: (domain: string) => void;
  handleTestingToggle: (domain: string) => void;
  handleThirdPartyToggle: (domain: string) => void;
  openPathsDialog: (params: {
    domain: string;
    editPath?: string;
    editKind?: PathScopeKind;
    editIsInFunctionalityScope?: boolean;
    editIsInTestingScope?: boolean;
    editIsThirdParty?: boolean;
  }) => void;
  requestRemovePath: (target: PathToRemove) => void;
  handleUpdatePrimaryHostPaths: (
    updater: (domain: DomainConfig) => DomainConfig,
  ) => void;
  updateDomain: (
    domainName: string,
    updater: (domain: DomainConfig) => DomainConfig,
  ) => void;
  setDomainToRemove: (domain: string | null) => void;
  handleRemoveDomain: () => void;
  handleConfirmRemovePath: () => void;
  setPathToRemove: (target: PathToRemove | null) => void;
  handleConfirmFirstTestingEnable: () => void;
  setPendingTestingEnableDomain: (domain: string | null) => void;
  handlePrimaryAttackChange: (enabled: boolean) => void;
};

export const useDomainScopeTableView = (
  params: UseDomainScopeTableViewParams,
) => {
  const t = useTranslations('designSystem.domainScope');
  const tCommon = useTranslations('designSystem.common');
  const {
    domains,
    discoveredSubdomains,
    trimmedPrimaryApplicationUrl,
    primaryHostname,
    primaryHostDomainEntry,
    redirectTargetHostname,
    primaryPathEntries,
    primaryAttackEnabled,
    secondaryDomains,
    isPrimaryExpanded,
    setIsPrimaryExpanded,
    expandedDomains,
    isReadOnly,
    renderDomainSuffix,
    pathsDialogState,
    domainToRemove,
    pathToRemove,
    pendingTestingEnableDomain,
    isDisablePrimaryAttackDialogOpen,
    setIsDisablePrimaryAttackDialogOpen,
    toggleExpanded,
    handlePrimaryAttackToggle,
    handleFunctionalityToggle,
    handleTestingToggle,
    handleThirdPartyToggle,
    openPathsDialog,
    requestRemovePath,
    handleUpdatePrimaryHostPaths,
    updateDomain,
    setDomainToRemove,
    handleRemoveDomain,
    handleConfirmRemovePath,
    setPathToRemove,
    handleConfirmFirstTestingEnable,
    setPendingTestingEnableDomain,
    handlePrimaryAttackChange,
  } = params;
  const pathsDialogEntry =
    pathsDialogState &&
    (pathsDialogState.domain === primaryHostname
      ? (primaryHostDomainEntry ?? {
          domain: primaryHostname,
          source: DomainSourceSchema.enum.USER_PROVIDED,
          isInFunctionalityScope: true,
          isInTestingScope: primaryAttackEnabled,
          isThirdParty: false,
          pathRules: [],
          hasWaf: null,
          createdAt: new Date(),
          enabledAt: null,
        })
      : domains.find(({ domain }) => domain === pathsDialogState.domain));
  const rowLabels = useMemo(
    () => ({
      automatic: t('automatic'),
      manual: t('manual'),
      enabled: t('enabled'),
      disabled: t('disabled'),
      configuredPerPath: t('configuredPerPath'),
      allPaths: t('allPaths'),
      partially: t('partially'),
      allowlist: t('allowlist'),
      blocklist: t('blocklist'),
      neverTested: t('neverTested'),
      addPaths: t('addPaths'),
      editPath: t('editPath'),
      deleteLabel: tCommon('delete'),
      primary: t('primary'),
      redirectsTo: (domain: string) => t('redirectsTo', { domain }),
      primaryInteractionAlwaysOn: t('tooltips.primaryInteractionAlwaysOn'),
    }),
    [t, tCommon],
  );
  const rows = useMemo(
    () =>
      buildDomainScopeTableRows({
        labels: rowLabels,
        secondaryDomains,
        discoveredSubdomains,
        trimmedPrimaryApplicationUrl,
        primaryHostname,
        primaryHostDomainEntry,
        redirectTargetHostname,
        primaryPathEntries,
        primaryAttackEnabled,
        isPrimaryExpanded,
        expandedDomains,
        isReadOnly,
        renderDomainSuffix,
        onTogglePrimaryExpanded: () =>
          setIsPrimaryExpanded((current) => !current),
        onToggleDomainExpanded: toggleExpanded,
        onPrimaryAttackToggle: handlePrimaryAttackToggle,
        onFunctionalityToggle: handleFunctionalityToggle,
        onTestingToggle: handleTestingToggle,
        onThirdPartyToggle: handleThirdPartyToggle,
        onAddPrimaryPaths: () => {
          if (!primaryHostname) {
            return;
          }

          openPathsDialog({ domain: primaryHostname });
        },
        onRemovePrimaryPath: (path) => {
          if (!primaryHostname) {
            return;
          }

          requestRemovePath({ domain: primaryHostname, path });
        },
        onEditPrimaryPath: ({
          path,
          kind,
          isInFunctionalityScope,
          isInTestingScope,
          isThirdParty,
        }) => {
          if (!primaryHostname) {
            return;
          }

          openPathsDialog({
            domain: primaryHostname,
            editPath: path,
            editKind: kind,
            editIsInFunctionalityScope: isInFunctionalityScope,
            editIsInTestingScope: isInTestingScope,
            editIsThirdParty: isThirdParty,
          });
        },
        onUpdatePrimaryHostPaths: handleUpdatePrimaryHostPaths,
        onOpenPathsDialog: openPathsDialog,
        onRemoveDomain: setDomainToRemove,
        onRequestRemovePath: requestRemovePath,
        onUpdateDomain: updateDomain,
      }),
    [
      discoveredSubdomains,
      expandedDomains,
      handleFunctionalityToggle,
      handlePrimaryAttackToggle,
      handleTestingToggle,
      handleThirdPartyToggle,
      handleUpdatePrimaryHostPaths,
      isPrimaryExpanded,
      isReadOnly,
      openPathsDialog,
      primaryAttackEnabled,
      primaryHostDomainEntry,
      primaryHostname,
      primaryPathEntries,
      redirectTargetHostname,
      renderDomainSuffix,
      requestRemovePath,
      rowLabels,
      secondaryDomains,
      setDomainToRemove,
      setIsPrimaryExpanded,
      toggleExpanded,
      trimmedPrimaryApplicationUrl,
      updateDomain,
    ],
  );
  const columns = useDomainScopeTableColumns();
  const emptyState = (
    <SettingsEmptyState
      title={t('emptyState.title')}
      icon={<FileSearchIcon size={56} />}
    >
      {t('emptyState.description')}
    </SettingsEmptyState>
  );
  const confirmationDialog = domainToRemove
    ? {
        variant: 'destructive' as const,
        title: t('removeDomainDialog.title'),
        description: t('removeDomainDialog.description'),
        confirmText: t('removeDomainDialog.confirm'),
        cancelText: tCommon('cancel'),
        onConfirm: handleRemoveDomain,
        onClose: () => setDomainToRemove(null),
      }
    : pathToRemove
      ? {
          variant: 'destructive' as const,
          title: t('removePathDialog.title'),
          descriptionContent: t.rich('removePathDialog.description', {
            path: pathToRemove.path,
            domain: pathToRemove.domain,
            bold: (chunks) => <BodyStrong as="span">{chunks}</BodyStrong>,
          }),
          confirmText: t('removePathDialog.confirm'),
          cancelText: tCommon('cancel'),
          onConfirm: handleConfirmRemovePath,
          onClose: () => setPathToRemove(null),
        }
      : pendingTestingEnableDomain
        ? {
            variant: 'info' as const,
            title: t('firstEnableModal.title'),
            description: t('firstEnableModal.description'),
            confirmText: t('firstEnableModal.confirm'),
            cancelText: t('firstEnableModal.cancel'),
            onConfirm: handleConfirmFirstTestingEnable,
            onClose: () => setPendingTestingEnableDomain(null),
          }
        : isDisablePrimaryAttackDialogOpen && primaryHostname
          ? {
              variant: 'destructive' as const,
              title: t('disablePrimaryAttackDialog.title', {
                domain: primaryHostname,
              }),
              description: t('disablePrimaryAttackDialog.description', {
                domain: primaryHostname,
              }),
              confirmText: t('disablePrimaryAttackDialog.confirm'),
              cancelText: t('disablePrimaryAttackDialog.cancel'),
              onConfirm: () => {
                handlePrimaryAttackChange(false);
                setIsDisablePrimaryAttackDialogOpen(false);
              },
              onClose: () => setIsDisablePrimaryAttackDialogOpen(false),
            }
          : null;

  return {
    columns,
    rows,
    emptyState,
    confirmationDialog,
    pathsDialogEntry,
  };
};

export type UseDomainScopeTableParams = {
  domains: DomainConfig[];
  discoveredSubdomains?: string[];
  appUrlHostname?: string | null;
  primaryApplicationUrl?: string | null;
  redirectToUrl?: string | null;
  isReadOnly?: boolean;
  onDomainsChange: (domains: DomainConfig[]) => void;
  renderDomainSuffix?: (domain: string) => ReactNode;
  isAddDomainDialogOpen?: boolean;
  onAddDomainDialogOpenChange?: (open: boolean) => void;
};

export const useDomainScopeTable = ({
  domains,
  discoveredSubdomains = [],
  appUrlHostname,
  primaryApplicationUrl,
  redirectToUrl,
  isReadOnly,
  onDomainsChange,
  renderDomainSuffix,
  isAddDomainDialogOpen,
  onAddDomainDialogOpenChange,
}: UseDomainScopeTableParams) => {
  const t = useTranslations('designSystem.domainScope');
  const [uncontrolledAddOpen, setUncontrolledAddOpen] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const clearDomainError = useCallback(() => {
    setDomainError(null);
  }, []);
  const [domainToRemove, setDomainToRemove] = useState<string | null>(null);
  const [pathToRemove, setPathToRemove] = useState<PathToRemove | null>(null);
  const [pathsDialogState, setPathsDialogState] = useState<{
    domain: string;
    editPath?: string;
    editKind?: PathScopeKind;
    editIsInFunctionalityScope?: boolean;
    editIsInTestingScope?: boolean;
    editIsThirdParty?: boolean;
  } | null>(null);
  const [pendingTestingEnableDomain, setPendingTestingEnableDomain] = useState<
    string | null
  >(null);
  const [
    isDisablePrimaryAttackDialogOpen,
    setIsDisablePrimaryAttackDialogOpen,
  ] = useState(false);
  const {
    trimmedPrimaryApplicationUrl,
    primaryHostname,
    primaryHostDomainEntry,
    primaryPathEntries,
    primaryAttackEnabled,
    redirectTargetHostname,
    secondaryDomains,
    isPrimaryExpanded,
    setIsPrimaryExpanded,
    expandedDomains,
    setExpandedDomains,
  } = useDomainScopeTableDerived({
    domains,
    appUrlHostname,
    primaryApplicationUrl,
    redirectToUrl,
  });
  const isAddControlled = typeof isAddDomainDialogOpen === 'boolean';
  const addDomainDialogOpen = isAddControlled
    ? isAddDomainDialogOpen
    : uncontrolledAddOpen;
  const setAddDomainDialogOpen = useCallback(
    (open: boolean) => {
      if (!isAddControlled) {
        setUncontrolledAddOpen(open);
      }

      onAddDomainDialogOpenChange?.(open);
    },
    [isAddControlled, onAddDomainDialogOpenChange],
  );

  useEffect(() => {
    if (addDomainDialogOpen) {
      setDomainError(null);
    }
  }, [addDomainDialogOpen]);
  const updateDomain = useCallback(
    (domainName: string, updater: (domain: DomainConfig) => DomainConfig) => {
      onDomainsChange(
        domains.map((entry) =>
          entry.domain === domainName ? updater(entry) : entry,
        ),
      );
    },
    [domains, onDomainsChange],
  );
  const toggleExpanded = useCallback(
    (domainName: string) => {
      setExpandedDomains((previous) => {
        const next = new Set(previous);

        if (next.has(domainName)) {
          next.delete(domainName);
        } else {
          next.add(domainName);
        }

        return next;
      });
    },
    [setExpandedDomains],
  );
  const handleFunctionalityToggle = useCallback(
    (domainName: string) => {
      if (isReadOnly) {
        return;
      }

      updateDomain(domainName, (entry) =>
        applyFunctionalityToggle({
          domain: entry,
          enabled: !getScopeFlag({ domain: entry, field: 'functionality' }),
        }),
      );
    },
    [isReadOnly, updateDomain],
  );
  const handleTestingToggle = useCallback(
    (domainName: string) => {
      if (isReadOnly) {
        return;
      }

      const entry = domains.find(({ domain }) => domain === domainName);

      if (!entry) {
        return;
      }

      const isCurrentlyOff = !getScopeFlag({ domain: entry, field: 'testing' });
      const isFirstTimeEnable = isCurrentlyOff && entry.enabledAt === null;

      if (isFirstTimeEnable) {
        setPendingTestingEnableDomain(domainName);

        return;
      }

      updateDomain(domainName, (current) =>
        applyTestingToggle({
          domain: current,
          enabled: !getScopeFlag({ domain: current, field: 'testing' }),
        }),
      );
    },
    [domains, isReadOnly, updateDomain],
  );
  const handleThirdPartyToggle = useCallback(
    (domainName: string) => {
      if (isReadOnly) {
        return;
      }

      updateDomain(domainName, (entry) =>
        applyThirdPartyToggle({
          domain: entry,
          enabled: !getScopeFlag({ domain: entry, field: 'thirdParty' }),
        }),
      );
    },
    [isReadOnly, updateDomain],
  );
  const handleConfirmFirstTestingEnable = () => {
    if (pendingTestingEnableDomain === null) {
      return;
    }

    updateDomain(pendingTestingEnableDomain, (entry) => ({
      ...applyTestingToggle({ domain: entry, enabled: true }),
      enabledAt: new Date(),
    }));
    setPendingTestingEnableDomain(null);
  };

  const handleAddDomain = (domain: string) => {
    if (isReadOnly) {
      return;
    }

    if (!isValidDomainHost(domain)) {
      setDomainError(t('invalidDomainError'));

      return;
    }

    const candidateHostname = getDomainConfigHostname(domain)?.toLowerCase();
    const isDuplicate =
      domains.some(
        ({ domain: existing }) =>
          getDomainConfigHostname(existing)?.toLowerCase() ===
          candidateHostname,
      ) ||
      isDomainLinkedToPrimaryHost({
        domain,
        primaryHostname,
        redirectToUrl,
      });

    if (isDuplicate) {
      setDomainError(t('duplicateError'));

      return;
    }

    setDomainError(null);
    onDomainsChange([
      ...domains,
      buildAddedDomainEntry({
        domain,
        primaryHost: primaryHostname,
      }),
    ]);
    setAddDomainDialogOpen(false);
  };

  const handleRemoveDomain = () => {
    if (!domainToRemove) {
      return;
    }

    onDomainsChange(domains.filter(({ domain }) => domain !== domainToRemove));
    setDomainToRemove(null);
  };

  const handleUpdatePrimaryHostPaths = useCallback(
    (updater: (domain: DomainConfig) => DomainConfig) => {
      if (isReadOnly || !primaryHostname) {
        return;
      }

      onDomainsChange(
        updatePrimaryHostDomainEntry({
          domains,
          hostname: primaryHostname,
          updater,
        }),
      );
    },
    [domains, isReadOnly, onDomainsChange, primaryHostname],
  );
  const handleConfirmRemovePath = () => {
    if (!pathToRemove) {
      return;
    }

    if (primaryHostname && pathToRemove.domain === primaryHostname) {
      handleUpdatePrimaryHostPaths((entry) =>
        removePathFromDomain({ domain: entry, path: pathToRemove.path }),
      );
    } else {
      updateDomain(pathToRemove.domain, (entry) =>
        removePathFromDomain({ domain: entry, path: pathToRemove.path }),
      );
    }

    setPathToRemove(null);
  };

  const requestRemovePath = useCallback((target: PathToRemove) => {
    setPathToRemove(target);
  }, []);
  const handleAddPaths = (paths: PathScopeDraft[]) => {
    if (!pathsDialogState || paths.length === 0) {
      return;
    }

    const { domain: pathsDialogDomain } = pathsDialogState;

    if (primaryHostname && pathsDialogDomain === primaryHostname) {
      handleUpdatePrimaryHostPaths((entry) =>
        addPathsToDomain({ domain: entry, paths }),
      );
      setIsPrimaryExpanded(true);
    } else {
      updateDomain(pathsDialogDomain, (entry) =>
        addPathsToDomain({ domain: entry, paths }),
      );
      setExpandedDomains((previous) =>
        new Set(previous).add(pathsDialogDomain),
      );
    }

    setPathsDialogState(null);
  };

  const handleEditPath = ({
    previousPath,
    path,
    kind,
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  }: {
    previousPath: string;
    path: string;
    kind: PathScopeKind;
    isInFunctionalityScope: boolean;
    isInTestingScope: boolean;
    isThirdParty: boolean;
  }) => {
    if (!pathsDialogState) {
      return;
    }

    const { domain: pathsDialogDomain } = pathsDialogState;

    if (primaryHostname && pathsDialogDomain === primaryHostname) {
      handleUpdatePrimaryHostPaths((entry) =>
        updatePathInDomain({
          domain: entry,
          previousPath,
          nextPath: path,
          kind,
          isInFunctionalityScope,
          isInTestingScope,
          isThirdParty,
        }),
      );
    } else {
      updateDomain(pathsDialogDomain, (entry) =>
        updatePathInDomain({
          domain: entry,
          previousPath,
          nextPath: path,
          kind,
          isInFunctionalityScope,
          isInTestingScope,
          isThirdParty,
        }),
      );
    }

    setPathsDialogState(null);
  };

  const openPathsDialog = useCallback(
    ({
      domain,
      editPath,
      editKind,
      editIsInFunctionalityScope,
      editIsInTestingScope,
      editIsThirdParty,
    }: {
      domain: string;
      editPath?: string;
      editKind?: PathScopeKind;
      editIsInFunctionalityScope?: boolean;
      editIsInTestingScope?: boolean;
      editIsThirdParty?: boolean;
    }) => {
      setPathsDialogState({
        domain,
        editPath,
        editKind,
        editIsInFunctionalityScope,
        editIsInTestingScope,
        editIsThirdParty,
      });
    },
    [],
  );
  const handlePrimaryAttackChange = useCallback(
    (enabled: boolean) => {
      if (isReadOnly || !primaryHostname) {
        return;
      }

      onDomainsChange(
        applyPrimaryHostAttackToggle({
          applicationUrl: trimmedPrimaryApplicationUrl,
          domains,
          hostname: primaryHostname,
          redirectToUrl,
          enabled,
        }),
      );
    },
    [
      domains,
      isReadOnly,
      onDomainsChange,
      primaryHostname,
      redirectToUrl,
      trimmedPrimaryApplicationUrl,
    ],
  );
  const handlePrimaryAttackToggle = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    if (primaryAttackEnabled) {
      setIsDisablePrimaryAttackDialogOpen(true);

      return;
    }

    handlePrimaryAttackChange(true);
  }, [handlePrimaryAttackChange, isReadOnly, primaryAttackEnabled]);

  const { columns, rows, emptyState, confirmationDialog, pathsDialogEntry } =
    useDomainScopeTableView({
      domains,
      discoveredSubdomains,
      trimmedPrimaryApplicationUrl,
      primaryHostname,
      primaryHostDomainEntry,
      redirectTargetHostname,
      primaryPathEntries,
      primaryAttackEnabled,
      secondaryDomains,
      isPrimaryExpanded,
      setIsPrimaryExpanded,
      expandedDomains,
      isReadOnly,
      renderDomainSuffix,
      pathsDialogState,
      domainToRemove,
      pathToRemove,
      pendingTestingEnableDomain,
      isDisablePrimaryAttackDialogOpen,
      setIsDisablePrimaryAttackDialogOpen,
      toggleExpanded,
      handlePrimaryAttackToggle,
      handleFunctionalityToggle,
      handleTestingToggle,
      handleThirdPartyToggle,
      openPathsDialog,
      requestRemovePath,
      handleUpdatePrimaryHostPaths,
      updateDomain,
      setDomainToRemove,
      handleRemoveDomain,
      handleConfirmRemovePath,
      setPathToRemove,
      handleConfirmFirstTestingEnable,
      setPendingTestingEnableDomain,
      handlePrimaryAttackChange,
    });

  return {
    t,
    columns,
    rows,
    emptyState,
    confirmationDialog,
    addDomainDialogOpen,
    setAddDomainDialogOpen,
    handleAddDomain,
    domainError,
    clearDomainError,
    pathsDialogEntry,
    pathsDialogIsPrimaryHost:
      Boolean(primaryHostname) && pathsDialogState?.domain === primaryHostname,
    pathsDialogState,
    setPathsDialogState,
    handleAddPaths,
    handleEditPath,
  };
};

type DomainScopeTableProps = {
  domains: DomainConfig[];
  discoveredSubdomains?: string[];
  appUrlHostname?: string | null;
  primaryApplicationUrl?: string | null;
  redirectToUrl?: string | null;
  isLoading?: boolean;
  isReadOnly?: boolean;
  tableMaxHeight?: string;
  onDomainsChange: (domains: DomainConfig[]) => void;
  rightContent?: ReactNode;
  renderDomainSuffix?: (domain: string) => ReactNode;
  isAddDomainDialogOpen?: boolean;
  onAddDomainDialogOpenChange?: (open: boolean) => void;
};

export const DomainScopeTable = ({
  domains,
  discoveredSubdomains = [],
  appUrlHostname,
  primaryApplicationUrl,
  redirectToUrl,
  isLoading,
  isReadOnly,
  tableMaxHeight = '300px',
  onDomainsChange,
  rightContent,
  renderDomainSuffix,
  isAddDomainDialogOpen,
  onAddDomainDialogOpenChange,
}: DomainScopeTableProps) => {
  const {
    t,
    columns,
    rows,
    emptyState,
    confirmationDialog,
    addDomainDialogOpen,
    setAddDomainDialogOpen,
    handleAddDomain,
    domainError,
    clearDomainError,
    pathsDialogEntry,
    pathsDialogIsPrimaryHost,
    pathsDialogState,
    setPathsDialogState,
    handleAddPaths,
    handleEditPath,
  } = useDomainScopeTable({
    domains,
    discoveredSubdomains,
    appUrlHostname,
    primaryApplicationUrl,
    redirectToUrl,
    isReadOnly,
    onDomainsChange,
    renderDomainSuffix,
    isAddDomainDialogOpen,
    onAddDomainDialogOpenChange,
  });

  return (
    <>
      <TableGrid
        columns={columns}
        rows={rows}
        placeholder={t('searchPlaceholder')}
        tableMaxHeight={tableMaxHeight}
        isLoading={isLoading}
        emptyState={emptyState}
        rightContent={rightContent}
      />

      <AddDomainDialog
        open={addDomainDialogOpen}
        onOpenChange={setAddDomainDialogOpen}
        onAddDomain={handleAddDomain}
        error={domainError}
        onDomainInputChange={clearDomainError}
      />

      {pathsDialogEntry && pathsDialogState ? (
        <AddPathsDialog
          open={pathsDialogState !== null}
          domain={pathsDialogEntry.domain}
          existingPaths={getPathEntries(pathsDialogEntry).map(
            ({ path }) => path,
          )}
          editPath={pathsDialogState.editPath}
          editKind={pathsDialogState.editKind}
          editIsInFunctionalityScope={
            pathsDialogState.editIsInFunctionalityScope
          }
          editIsInTestingScope={pathsDialogState.editIsInTestingScope}
          editIsThirdParty={pathsDialogState.editIsThirdParty}
          isPrimaryHost={pathsDialogIsPrimaryHost}
          onOpenChange={(open) => {
            if (!open) {
              setPathsDialogState(null);
            }
          }}
          onAddPaths={handleAddPaths}
          onEditPath={handleEditPath}
        />
      ) : null}

      {confirmationDialog ? (
        <ConfirmationDialog
          maxWidth="500px"
          isOpen
          onOpenChange={({ open }) => {
            if (!open) {
              confirmationDialog.onClose();
            }
          }}
          onConfirm={confirmationDialog.onConfirm}
          variant={confirmationDialog.variant}
          title={confirmationDialog.title}
          description={
            confirmationDialog.descriptionContent
              ? undefined
              : confirmationDialog.description
          }
          descriptionContent={confirmationDialog.descriptionContent}
          confirmText={confirmationDialog.confirmText}
          cancelText={confirmationDialog.cancelText}
          confirmAutomationId={
            confirmationDialog.variant === 'info'
              ? ApplicationAutomationIds.domainScopeFirstEnableConfirm
              : undefined
          }
        />
      ) : null}
    </>
  );
};
